import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import {
  addPendingTrade,
  removePendingTrade
} from '../../../redux/slices/pending';
import ProposalDisplay from './ProposalDisplay';
import {
  AsIs,
  CloseButton,
  CloseIcon,
  Header,
  HeaderMain,
  Make,
  MakeModel,
  Model,
  NegotiationContent,
  StyledNegotiationEnded,
  StyledProposal,
  StyledYourOffer,
  StyledYourOfferLabel,
  StyledYourOfferValue,
  SubHeader,
  Year,
  NewOfferSeparator
} from './Styles';
import convertToCurrency from '../../../_shared/utils/convertToCurrency';
import BaseModal from '../../../_shared/components/BaseModal';
import useAuctionNegotiation from '../../../_shared/hooks/trade/useAuctionNegotiation';
import NegotiationState from '../../../_shared/constants/negotiationState';
import ReducerConstants from '../../../_shared/constants/reducers';
import { TradeContext, TradeState } from '../../../_shared/types/trade';
import AddProposalForm from './AddProposalForm';
import ReplyNegotiationForm from './ReplyNegotiationForm';
import SecondBidderForm from './SecondBidderForm';
import {
  NegotiationActionType,
  NegotiationProposalUserType
} from '../../../_shared/models/NegotiationProposal';
import useNegotiationOfferLabels from '../../../_shared/hooks/trade/useNegotiationOfferLabels';
import isOmpPlatform from '../../../_shared/utils/isOmpPlatform';

const propTypes = {
  modalIsOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  tradeDetails: PropTypes.oneOfType([PropTypes.object]).isRequired,
  tradeContext: PropTypes.oneOf([TradeContext.Buy, TradeContext.Sell]),
  isOmp: PropTypes.bool
};

// tradeContext is temporarily missing from NegotiationModalButtonContainer. This should be removed once addressed.
const defaultProps = {
  tradeContext: null,
  isOmp: false
};

const FOOTER_VARIATION_STATE = {
  START_NEGOTIATION: 0,
  REACT_OFFER: 1,
  SHOW_MY_OFFER: 2,
  SECOND_BIDDER_FINISH_NEGOTIATION: 3
};

const NegotiationModal = ({
  modalIsOpen,
  onClose,
  tradeDetails,
  tradeContext,
  isOmp
}) => {
  const dispatch = useDispatch();
  const auth = useSelector(state => state[ReducerConstants.AUTH], shallowEqual);

  const refLastMessage = useRef();
  const isSellContext = tradeContext === TradeContext.Sell;
  const isBuyContext = tradeContext === TradeContext.Buy;

  const {
    negotiationState,
    auctionId,
    id: tradeId,
    asIs,
    isAsIs,
    isGreyOut
  } = tradeDetails;

  const tradeAsIs = asIs || isAsIs;

  const negotiations = useAuctionNegotiation(auctionId);
  const [offerTitle] = useNegotiationOfferLabels(
    negotiationState,
    tradeContext,
    isGreyOut
  );

  const [proposalList, setProposalList] = useState([]);
  const [myOffer, setMyOffer] = useState(null);
  const [latestOffer, setLatestOffer] = useState(null);
  const isCurrentUserSecondBidder =
    negotiations?.canAccept &&
    String(negotiations?.negotiationState?.toLowerCase()) ===
      String(NegotiationState.FINAL_ASKING_PRICE);

  const [footerVariation, setFooterVariation] = useState(
    FOOTER_VARIATION_STATE.START_NEGOTIATION
  );
  const [onAccept, setOnAccept] = useState(false);

  const scrollToBottom = () => {
    if (refLastMessage.current) {
      refLastMessage.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const showProxyAmount =
    negotiations?.proxyAmount &&
    negotiations?.topBid &&
    negotiations?.proxyAmount > negotiations?.topBid;

  useEffect(() => {
    const proposals = negotiations?.proposals || [];

    setProposalList(proposals);
    const latestOfferTemp = proposals?.slice(-1)[0];
    setLatestOffer(latestOfferTemp);

    if (latestOfferTemp) {
      const cannotNegotiate = !(
        negotiations?.canAccept ||
        negotiations?.canCounter ||
        negotiations?.canReject ||
        negotiations?.canStayFirm
      );

      if (cannotNegotiate) {
        setMyOffer(latestOfferTemp);
        setFooterVariation(FOOTER_VARIATION_STATE.SHOW_MY_OFFER);
      } else {
        if (proposals?.length > 1) {
          setMyOffer(proposals?.slice(-2)[0]);
        }

        if (!isSellContext && isCurrentUserSecondBidder) {
          setFooterVariation(
            FOOTER_VARIATION_STATE.SECOND_BIDDER_FINISH_NEGOTIATION
          );
        } else {
          setFooterVariation(FOOTER_VARIATION_STATE.REACT_OFFER);
        }
      }
    }
  }, [
    auth?.user?.id,
    negotiations,
    isSellContext,
    isCurrentUserSecondBidder,
    auctionId
  ]);

  useEffect(() => {
    scrollToBottom();
  });

  useEffect(() => {
    if (onAccept) dispatch(removePendingTrade(tradeId));
  }, [onAccept, dispatch, tradeId]);

  const onNewProposal = newProposal => {
    const pendingTradeDetails = { ...tradeDetails };
    const noNegotiation = negotiationState === NegotiationState.NO_NEGOTIATION;
    const buyerFirm = newProposal?.stayFirm;

    const sellerLastNegotiated =
      isBuyContext &&
      [NegotiationState.SELLER_COUNTER, NegotiationState.SELLER_OFFER].includes(
        negotiationState
      );

    const buyerLastNegotiated =
      isSellContext &&
      [
        NegotiationState.BUYER_COUNTER,
        NegotiationState.BUYER_FIRM,
        NegotiationState.BUYER_OFFER
      ].includes(negotiationState);

    if (noNegotiation) {
      pendingTradeDetails.negotiationState = isSellContext
        ? NegotiationState.SELLER_OFFER
        : NegotiationState.BUYER_OFFER;
    }

    if (buyerLastNegotiated) {
      pendingTradeDetails.negotiationState = NegotiationState.SELLER_COUNTER;
    }

    if (sellerLastNegotiated) {
      pendingTradeDetails.negotiationState = NegotiationState.BUYER_COUNTER;
    }

    if (buyerFirm) {
      pendingTradeDetails.negotiationState = NegotiationState.BUYER_FIRM;
    }

    pendingTradeDetails.negotiationOffer = newProposal.price;
    pendingTradeDetails.sellerNegotiated = newProposal.isUserSeller;
    dispatch(removePendingTrade(tradeId));
    dispatch(addPendingTrade(pendingTradeDetails));
  };

  const findSellerLowestOffer = () => {
    let lowestOffer = null;
    proposalList
      .filter(
        p =>
          p.recipient === NegotiationProposalUserType.TOP_BIDDER &&
          p.initiator === NegotiationProposalUserType.SELLER
      )
      .forEach(sellerProposal => {
        if (lowestOffer === null) {
          lowestOffer = sellerProposal.price;
        }
        lowestOffer =
          sellerProposal.price < lowestOffer
            ? sellerProposal.price
            : lowestOffer;
      });

    return lowestOffer;
  };

  const onNegotiationEnded = newTradeState => {
    dispatch(removePendingTrade(tradeId));
    const pendingTradeDetails = { ...tradeDetails };
    switch (newTradeState) {
      case TradeState.pending:
        pendingTradeDetails.negotiationState =
          NegotiationState.FINAL_ASKING_PRICE;
        if (isBuyContext) {
          pendingTradeDetails.negotiationOffer = findSellerLowestOffer();
        }
        break;
      case TradeState.lost:
        pendingTradeDetails.negotiationState = NegotiationState.DECLINED;
        break;
      default:
        // Won, Arrived
        return;
    }

    pendingTradeDetails.isGreyOut = isBuyContext;
    dispatch(addPendingTrade(pendingTradeDetails));
  };

  const getNegotiationFooter = variation => {
    switch (variation) {
      case FOOTER_VARIATION_STATE.START_NEGOTIATION:
        return (
          <AddProposalForm
            tradeDetails={tradeDetails}
            tradeContext={tradeContext}
            negotiations={negotiations}
            onAddProposal={onNewProposal}
            proxyAmount={showProxyAmount && negotiations?.proxyAmount}
            highBidThreshold={negotiations?.highBidThreshold}
            close={onClose}
          />
        );
      case FOOTER_VARIATION_STATE.REACT_OFFER:
        return (
          <ReplyNegotiationForm
            latestOffer={latestOffer}
            tradeDetails={tradeDetails}
            negotiations={negotiations}
            onAddCounterOffer={onNewProposal}
            myLatestOffer={myOffer ? myOffer.price : negotiations?.topBid}
            close={onClose}
            tradeContext={tradeContext}
            highBidThreshold={negotiations?.highBidThreshold}
            setOnAccept={setOnAccept}
            onNegotiationEnded={onNegotiationEnded}
            isOmp={isOmp}
          />
        );
      case FOOTER_VARIATION_STATE.SHOW_MY_OFFER:
        return (
          <StyledYourOffer>
            <StyledYourOfferLabel>{offerTitle}</StyledYourOfferLabel>
            <StyledYourOfferValue>
              {convertToCurrency(myOffer.price)}
            </StyledYourOfferValue>
          </StyledYourOffer>
        );
      case FOOTER_VARIATION_STATE.SECOND_BIDDER_FINISH_NEGOTIATION:
        return (
          <SecondBidderForm
            latestOffer={latestOffer}
            tradeDetails={tradeDetails}
            close={onClose}
            setOnAccept={setOnAccept}
            onNegotiationEnded={onNegotiationEnded}
          />
        );
      default:
        return null;
    }
  };

  const proposalIsFromHost = proposal => {
    return (
      (proposal.isUserSeller && isSellContext) ||
      (!proposal.isUserSeller && !isSellContext)
    );
  };

  const showNewOffer =
    (isBuyContext &&
      [NegotiationState.SELLER_COUNTER, NegotiationState.SELLER_OFFER].includes(
        negotiationState
      )) ||
    (isSellContext &&
      [NegotiationState.BUYER_COUNTER, NegotiationState.BUYER_OFFER].includes(
        negotiationState
      ));

  return (
    <BaseModal
      isOpen={modalIsOpen}
      width={467}
      height={461}
      isClickEscClose
      isClickOverlayClose
      onRequestClose={onClose}
      padding="0px"
      hasParentSelector={isOmp}
    >
      <Header>
        <HeaderMain>
          <CloseButton onClick={onClose}>
            <CloseIcon />
          </CloseButton>
          {tradeAsIs && (
            <div className="vdp-asis">
              <AsIs>
                <FormattedMessage id="vdp.asis" />
              </AsIs>
            </div>
          )}
          <Year>{tradeDetails?.year}</Year>
          <MakeModel showAsIsIcon={tradeAsIs}>
            <Make>
              {tradeDetails?.make?.name || tradeDetails?.make?.displayName}
            </Make>
            <Model>
              {tradeDetails?.model?.name || tradeDetails?.model?.displayName}
            </Model>
          </MakeModel>
        </HeaderMain>
        <SubHeader>
          {negotiations?.topBid && (
            <div>
              <FormattedMessage
                id={
                  isSellContext
                    ? 'modal.negotiation.lastBid'
                    : 'list.card.negotiation.yourTopBid'
                }
              />
              : <b>{convertToCurrency(negotiations?.topBid)}</b>
            </div>
          )}
          {negotiations?.floorPrice && (
            <div>
              <FormattedMessage id="modal.negotiation.floorPrice" />:{' '}
              <b>{convertToCurrency(negotiations?.floorPrice)}</b>
            </div>
          )}
          {showProxyAmount && (
            <div>
              <FormattedMessage id="modal.negotiation.proxyAmount" />:{' '}
              <b>{convertToCurrency(negotiations?.proxyAmount)}</b>
            </div>
          )}
        </SubHeader>
      </Header>
      {FOOTER_VARIATION_STATE.SECOND_BIDDER_FINISH_NEGOTIATION !==
        footerVariation && (
        <NegotiationContent>
          <div className="content">
            {proposalList.length === 0 ? (
              <FormattedMessage id="modal.negotiation.noOffers" />
            ) : (
              proposalList.map((proposal, index) => (
                <React.Fragment key={`proposal-${proposal.id}`}>
                  {index === proposalList.length - 1 && showNewOffer && (
                    <NewOfferSeparator>
                      <span>
                        <FormattedMessage
                          id={
                            isOmpPlatform()
                              ? 'vdp.pending.seller.label.new'
                              : 'vdp.pending.seller.label.buyerOffer'
                          }
                        />
                      </span>
                    </NewOfferSeparator>
                  )}
                  <StyledProposal
                    isHost={proposalIsFromHost(proposal)}
                    ref={refLastMessage}
                  >
                    <ProposalDisplay
                      isSellContext={isSellContext}
                      proposal={proposal}
                    />
                    <StyledProposal.Bubble
                      isHost={proposalIsFromHost(proposal)}
                    >
                      <StyledProposal.Offer>
                        {proposal.stayFirm && (
                          <FormattedMessage
                            id="modal.negotiation.stayFirm.stayFirmLabel"
                            values={{
                              offer: convertToCurrency(proposal.price)
                            }}
                          />
                        )}
                        {String(NegotiationActionType.PROPOSAL) ===
                          String(proposal.actionType) &&
                          !proposal.stayFirm &&
                          convertToCurrency(proposal.price)}
                        {String(NegotiationActionType.FINAL_ASKING_PRICE) ===
                          String(proposal.actionType) && (
                          <FormattedMessage
                            id="modal.negotiation.sentToSecondBidder"
                            values={{
                              price: convertToCurrency(proposal.price)
                            }}
                          />
                        )}
                        {[
                          NegotiationActionType.DECLINED,
                          NegotiationActionType.TIMER_EXPIRED
                        ].includes(proposal.actionType) && (
                          <FormattedMessage id="vdp.pending.buyer.declined" />
                        )}
                      </StyledProposal.Offer>
                      <StyledProposal.Comment>
                        {proposal.comment}
                      </StyledProposal.Comment>
                    </StyledProposal.Bubble>
                  </StyledProposal>
                  {[
                    NegotiationActionType.DECLINED,
                    NegotiationActionType.TIMER_EXPIRED
                  ].includes(proposal.actionType) && (
                    <StyledNegotiationEnded>
                      <FormattedMessage id="modal.negotiation.negotiationEnded" />
                    </StyledNegotiationEnded>
                  )}
                </React.Fragment>
              ))
            )}
          </div>
        </NegotiationContent>
      )}
      {![
        NegotiationActionType.DECLINED,
        NegotiationActionType.TIMER_EXPIRED
      ].includes(latestOffer?.actionType) &&
        !(
          isSellContext &&
          [NegotiationActionType.FINAL_ASKING_PRICE].includes(
            latestOffer?.actionType
          )
        ) &&
        getNegotiationFooter(footerVariation)}
    </BaseModal>
  );
};

NegotiationModal.propTypes = propTypes;
NegotiationModal.defaultProps = defaultProps;

export default NegotiationModal;
